import React, { useEffect, useMemo, useRef, useState } from "react";
import { COINS } from "../../../utils/constants";

const COIN_COLORS: Record<string, string> = Object.fromEntries(
  (COINS as any[]).map((c: any) => [c.symbol, c.color])
);

const LEVERAGE_BADGE: Record<string, string> = {
  BTC: "20X", ETH: "20X", XRP: "3X", SOL: "5X",
  BNB: "10X", ADA: "10X", DOGE: "10X", DOT: "3X",
};

const FAV_KEY = "xt-fav-pairs";

const loadFavs = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
  } catch {
    return [];
  }
};

const fmtVol = (v: number) => {
  if (!v) return "—";
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
  return v.toFixed(2);
};

const fmtPrice = (p: number) =>
  !p ? "—" : p < 1 ? p.toFixed(4) : p.toLocaleString(undefined, { maximumFractionDigits: 2 });

interface Props {
  selectedCoin: string;
  prices: Record<string, number>;
  marketData: Record<string, any>;
  onSelect: (s: string) => void;
}

/**
 * PairSelector — XT.com-style pair picker.
 * Trigger: pair name + chevron. Panel: search, tabs (USDT / Favorites / Hot),
 * column headers, rows with star, coin icon, leverage badge, volume,
 * last price, 24h change.
 */
const PairSelector: React.FC<Props> = ({ selectedCoin, prices, marketData, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<"usdt" | "fav" | "hot">("usdt");
  const [favs, setFavs] = useState<string[]>(loadFavs);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleFav = (sym: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavs((prev) => {
      const next = prev.includes(sym) ? prev.filter((s) => s !== sym) : [...prev, sym];
      try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const rows = useMemo(() => {
    let list = (COINS as any[]).map((c: any) => ({
      symbol: c.symbol,
      name: c.name,
      price: prices[c.symbol] || 0,
      change: marketData[c.symbol]?.change_24h ?? 0,
      volume: marketData[c.symbol]?.volume_24h ?? 0,
    }));
    if (tab === "fav") list = list.filter((r) => favs.includes(r.symbol));
    if (tab === "hot") list = [...list].sort((a, b) => Math.abs(b.change) - Math.abs(a.change)).slice(0, 5);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((r) => r.symbol.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
    return list;
  }, [tab, favs, query, prices, marketData]);

  const Star: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill={filled ? "currentColor" : "none"}
      stroke="currentColor" strokeWidth={1.6}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );

  return (
    <div ref={rootRef} className="relative min-w-0">
      {/* Trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 group"
        title="Select pair"
      >
        <span className="text-gray-50 font-mono font-bold text-[13px]">
          {selectedCoin}/USDT
        </span>
        <svg viewBox="0 0 24 24" className={`w-3 h-3 text-gray-400 group-hover:text-gray-50 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute left-0 top-full mt-2 w-[380px] max-w-[92vw] bg-card border border-gray-700 rounded-xl shadow-card z-50 overflow-hidden">
          {/* Search */}
          <div className="p-3 pb-2">
            <div className="flex items-center gap-2 bg-panel border border-gray-700 rounded-lg px-2.5 py-1.5">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none"
                stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter the token or contract address"
                className="bg-transparent outline-none text-[12px] text-gray-50 placeholder-gray-500 w-full"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-4 px-3 pb-1.5 text-[11px] font-bold">
            {([["usdt", "USDT"], ["fav", "★ Favorites"], ["hot", "HOT COIN"]] as const).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={tab === id ? "text-gray-50" : "text-gray-500 hover:text-gray-300"}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-[9px] font-semibold tracking-wider text-gray-500 border-b border-gray-700/60">
            <span>PAIRS / VOLUME</span>
            <span className="text-right w-[84px]">LAST PRICE</span>
            <span className="text-right w-[70px]">24H CHANGE</span>
          </div>

          {/* Rows */}
          <div className="max-h-[320px] overflow-y-auto">
            {rows.length === 0 && (
              <div className="px-3 py-6 text-center text-[11px] text-gray-500">
                No pairs found
              </div>
            )}
            {rows.map((r) => {
              const isUp = r.change >= 0;
              const fav = favs.includes(r.symbol);
              return (
                <button
                  key={r.symbol}
                  onClick={() => { onSelect(r.symbol); setOpen(false); }}
                  className={`w-full grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 hover:bg-gray-900 transition-colors text-left ${
                    r.symbol === selectedCoin ? "bg-gray-900" : ""
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      onClick={(e) => toggleFav(r.symbol, e)}
                      className={fav ? "text-primary" : "text-gray-600 hover:text-gray-400"}
                    >
                      <Star filled={fav} />
                    </span>
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black shrink-0"
                      style={{ background: `${COIN_COLORS[r.symbol]}22`, color: COIN_COLORS[r.symbol] }}
                    >
                      {r.symbol[0]}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="text-[12px] font-bold text-gray-50">
                          {r.symbol}
                          <span className="text-gray-500 font-medium">/USDT</span>
                        </span>
                        {LEVERAGE_BADGE[r.symbol] && (
                          <span className="text-[8px] font-bold px-1 py-px rounded bg-gray-800 text-gray-400">
                            {LEVERAGE_BADGE[r.symbol]}
                          </span>
                        )}
                      </span>
                      <span className="block text-[9px] text-gray-500 font-mono">
                        {fmtVol(r.volume)}
                      </span>
                    </span>
                  </span>
                  <span className="text-right w-[84px] text-[12px] font-mono text-gray-50">
                    {fmtPrice(r.price)}
                  </span>
                  <span className={`text-right w-[70px] text-[12px] font-mono font-semibold ${isUp ? "text-up" : "text-down"}`}>
                    {isUp ? "+" : ""}{r.change.toFixed(2)}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PairSelector;
